<template>
  <form class="vp-message-drafting" :style="rootStyles">
    <div class="vp-message-drafting__content">
      <VpInput 
        v-show="showSubject" 
        class="vp-message-drafting__subject" 
        ref="subject" 
        v-model="internalSubject" 
        :readonly="previewMode || readonly"
        :disabled="disabled"
        :class="{ error: !previewMode && fieldError.subject }" 
        :placeholder="placeholderSubject" 
        :maxlength="limitSubject" 
        :show-characters-remaining="!previewMode && showSubjectCharactersRemaining"
      >
        <template #label>Subject Line {{ previewMode ? 'Preview' : '' }}</template>
        <template #error v-if="!previewMode && fieldError.subject">Please add a subject</template>
      </VpInput>
      <template v-if="showBody">
        <vp-textarea
          v-show="!previewMode"
          class="vp-message-drafting__body" 
          ref="body" 
          v-model="internalBody" 
          :readonly="previewMode || readonly"
          :disabled="disabled"
          :class="{ error: !previewMode && fieldError.body }" 
          :placeholder="placeholderBody" 
          :maxlength="limitBody" 
          :show-characters-remaining="!previewMode && showBodyCharactersRemaining"
          @blur="handleBlur()"
        >
          <template #label>Message Body</template>
          <template #error v-if="!previewMode && fieldError.body">Please add a body</template>
        </vp-textarea>
        <div v-show="previewMode" class="vp-message-drafting__textarea">
          <label class="vp-message-drafting__textarea-label" for="previewBody">Message Body Preview</label>
          <p class="vp-message-drafting__textarea-input" v-html="internalBody" readonly disabled></p>
        </div>
      </template>
    </div>
    <VpMessageInserts v-if="inserts.length" v-bind="insertProps" @insert-variable-clicked="handleTemplateInsert" @modal-variable-clicked="handleTemplateInsertModal" />
  </form>
</template>

<script>
import VpMessageInserts from './VpMessageInserts.vue';

export default {
  name: 'VpMessageDrafting',
  components: { VpMessageInserts },
  emits: ['input'],
  data() {
    return { 
      pendingResponse: false,
      internalSubject: this.subject || '', 
      internalBody: this.body || '', 
      fieldError: { subject: false, body: false } 
    };
  },
  computed: {
    isDisabled() {
      return this.disabled || this.readonly;
    },
    insertProps() {
      const { inserts, tab:formatTab, previewMode, readonly, disabled } = this;
      return { inserts, formatTab, previewMode, readonly, disabled }
    },
    rootStyles() {
      return {
        '--content-gap': this.styles.gap || '2rem',
      }
    },
  },
  props: {
    inserts: { 
      type: Array, 
      default: () => [],
      validator: (inserts) => {
        const isValid = inserts.every((item) => 'name' in item && 'prettyName' in item);
        if (!isValid) console.error(`Invalid prop: each insert must include a 'name' and 'prettyName' property, Received`, { inserts });
        return isValid;
      }
    },
    tab: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    isDirty: {
      type: Boolean,
      default: true,
    },
    skipValidation: {
      /** Used when only one message template is required to be valid, or as an override */
      type: Boolean,
      default: false,
    },
    /** Subject Props */
    subject: {
      type: String,
      default: ''
    },
    showSubject: {
      type: Boolean,
      default: true,
    },
    showSubjectCharactersRemaining: {
      type: Boolean,
      default: true,
    },
    limitSubject: {
      type: Number,
      default: 150,
    },
    placeholderSubject: {
      type: String,
      default: 'Enter Subject Line',
    },
    /** Body Props */
    insertResponse: {
      type: [String, Object],
      default: null
    },
    body: {
      type: String,
      default: ''
    },
    limitBody: {
      type: Number,
      default: 1800,
    },
    showBody: {
      type: Boolean,
      default: true,
    },
    showBodyCharactersRemaining: {
      type: Boolean,
      default: true,
    },
    placeholderBody: {
      type: String,
      default: 'Enter Message Body',
    },
    styles: {
      type: Object,
      default: () => ({}),
    }
  },
  watch: {
    body(text) {
      this.internalBody = text || '';
      /** Regenerate when switching between tabFormats */
      if (this.previewMode) this.switchBody();
    },
    subject(text) {
      this.internalSubject = text;
    },
    internalBody(text) {
      this.update();
      this.checkFieldError('body');
    },
    internalSubject(text) {
      this.update();
      this.checkFieldError('subject');
    },
    insertResponse(res) {
      if (this.pendingResponse) {
        this.pendingResponse = false;
        this.handleTemplateInsert(res);
      }
    },
    skipValidation() {
      return this.checkFieldError();
    },
    tab() {
      return this.checkFieldError();
    },
    previewMode: {
      immediate: true,
      handler() {
        return this.switchBody();
      }
    }
  },
  methods: {
    handleTemplateInsert(insert, suppressCurlies) {
      const value = ['string', 'number'].includes(typeof insert) ? insert : insert?.variable || insert?.value || insert?.name || '';
      if (this.isDisabled || !value) return null;
      let textAreaComponent = this.$refs.body;
      const textAreas = textAreaComponent.$el.getElementsByTagName('textarea');
      if (textAreas.length) {
        const textArea = textAreas[0];
        const insertLocation = textArea.selectionStart;
        if ((insertLocation === 0) || (insertLocation === null) || (insertLocation === undefined)) {
          if (suppressCurlies === 1) {
            this.internalBody += `${value}`;
          } else {
            this.internalBody += `{{${value}}}`;
          }
        } else if (suppressCurlies === 1) {
          this.internalBody = `${this.internalBody.substr(0, insertLocation)}${value}${this.internalBody.substr(insertLocation)}`;
        } else {
          this.internalBody = `${this.internalBody.substr(0, insertLocation)}{{${value}}}${this.internalBody.substr(insertLocation)}`;
        }
        textArea.focus();
      }
    },
    handleTemplateInsertModal(insert) {
      if (this.isDisabled) return null;
      const { name, handler } = insert || {};
      this.pendingResponse = true;
      if (handler) handler(this.updateBody);
      else if (name === 'buttonLink') this.$emit('openModal', 'add-button-link');
      else if (name === 'hyperlink') this.$emit('openModal', 'add-hyperlink');
      else if (name === 'emailLink') this.$emit('openModal', 'add-email-link');
      else if (name === 'image')  this.$emit('openModal', 'add-image');
    },
    switchBody() {
      this.internalBody = (this.previewMode) ? this.generatePreviewBody() : (this.body || '');
    },
    generatePreviewBody() {
      let text = this.sanitizeMessage(this.internalBody) || '';
      for (const insert of this.inserts) {
        let value = insert?.test;
        if (this.tab === 'email' && insert?.testEmail) value = insert.testEmail;
        if (this.tab === 'sms' && insert?.testSms) value = insert.testSms;
        if (value) text = text.replaceAll(`{{${insert.name}}}`, value);
      }
      text = text.replaceAll('\n', '<br>');
      return text;
    },
    sanitizeMessage(text) {
      if (!text) return text;
      // Functions the same as Angular version, but not sure if it's original intention. All HTML tags are removed.
      const sanitizedMsg = this.$sanitize(text);
      // $sanitize converts certain characters to $# codes, the next two lines convert those back to the proper characters
      // for display in the textarea, since textarea cannot be coerced into rendering html
      const parser = new DOMParser();
      const parsedMsg = parser.parseFromString(sanitizedMsg, 'text/html');
      return parsedMsg.body.innerHTML;
    },
    checkFieldError(type) {
      if (this.skipValidation) {
        this.fieldError.subject = '';
        this.fieldError.body = '';
      } else if (this.isDirty) {
        if (!type || type === 'subject') this.fieldError.subject = this.internalSubject === '';
        if (!type || type === 'body') this.fieldError.body = this.internalBody === '';
      }
    },
    clearFieldErrors() {
      this.fieldError.subject = false;
      this.fieldError.body = false;
    },
    handleBlur() {
      this.internalBody = this.sanitizeMessage(this.internalBody);
    },
    update() {
      if (this.isDisabled || this.previewMode) return null;
      return this.$emit('input', { body: this.internalBody, subject: this.internalSubject, format: this.tab });
    },
    updateBody(value) {
      return this.handleTemplateInsert(value, 1);
    }
  }
}
</script>

<style lang="scss">
  .vp-message-drafting {
    gap: 1.5rem;
    display: flex;
    justify-content: space-between;
    &__content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--content-gap);
    }
    .error {
      .vcl-input__input {
        border: 1px solid #CE3C31 !important;
      }
      .vcl-textarea__element {
        border: 1px solid #CE3C31 !important;
      }
    }
    &__textarea {
      /** Mock VpTextArea default styling */
      width: 100%;
      height: 100%;
      display: flex;
      text-align: left;
      max-height: 223px;
      flex-direction: column;
      &-label {
        color: #4f4f4f;
        user-select: none;
        box-sizing: border-box;
        margin: 0 0 1px 4px;
      }
      &-input {
        cursor: text;
        height: 200px;
        line-height: 1.5;
        overflow-y: auto;
        border-radius: 6px;
        border: 1px solid #c7c7c7;
        padding: 0.5rem 0.75rem !important;
        background-color: inherit !important;
      }
    }
  }
</style>